import React, { useEffect, useState } from 'react'
import { motion } from 'framer-motion'
import { TrendingUp, TrendingDown, Minus, Activity } from 'lucide-react'
import { apiFetch } from '../lib/api'

interface IndexData {
  name: string
  changePercent: number
  loading: boolean
  error: string | null
}

interface FearGreedData {
  score: number
  rating: string
}

const MarketIndices: React.FC = () => {
  const [indices, setIndices] = useState<IndexData[]>([
    { name: 'S&P 500', changePercent: 0, loading: true, error: null },
    { name: 'S&P 500 VIX', changePercent: 0, loading: true, error: null },
    { name: 'MSCI World', changePercent: 0, loading: true, error: null },
    { name: 'Hang Seng', changePercent: 0, loading: true, error: null },
    { name: 'SET', changePercent: 0, loading: true, error: null },
    { name: 'VN 30', changePercent: 0, loading: true, error: null },
  ])
  const [showVixPopover, setShowVixPopover] = useState(false)
  const [fearGreed, setFearGreed] = useState<FearGreedData | null>(null)
  const [fearGreedLoading, setFearGreedLoading] = useState(true)
  const fetchAllIndices = async () => {
    try {
      // Fetch from backend API
      const response = await apiFetch('/api/market-indices')

      if (!response.ok) {
        throw new Error('Failed to fetch indices')
      }

      const data = await response.json()

      // Update indices with fetched data
      setIndices(prevIndices =>
        prevIndices.map((index, i) => {
          const fetchedIndex = data.indices[i]
          return {
            ...index,
            changePercent: fetchedIndex?.changePercent || 0,
            loading: false,
            error: null
          }
        })
      )
    } catch (error) {
      console.error('Error fetching indices:', error)

      // Set error state
      setIndices(prevIndices =>
        prevIndices.map(index => ({
          ...index,
          loading: false,
          error: 'Failed'
        }))
      )
    }
  }

  const fetchFearGreed = async () => {
    try {
      setFearGreedLoading(true)
      const response = await fetch('https://production.dataviz.cnn.io/index/fearandgreed/graphdata')

      if (!response.ok) {
        throw new Error('Failed to fetch Fear & Greed Index')
      }

      const jsonData = await response.json()
      const fearGreedData = jsonData.fear_and_greed

      setFearGreed({
        score: fearGreedData.score || 0,
        rating: fearGreedData.rating,
      })
    } catch (err) {
      console.error('Error fetching Fear & Greed Index:', err)
      setFearGreed(null)
    } finally {
      setFearGreedLoading(false)
    }
  }

  useEffect(() => {
    fetchAllIndices()
    fetchFearGreed()

    const handleRefresh = () => {
      fetchAllIndices()
      fetchFearGreed()
    }

    window.addEventListener('refresh-market-data', handleRefresh)
    return () => {
      window.removeEventListener('refresh-market-data', handleRefresh)
    }
  }, [])

  const renderChangeIndicator = (changePercent: number) => {
    if (changePercent > 0) {
      return <TrendingUp className="w-3.5 h-3.5 text-gain" />
    } else if (changePercent < 0) {
      return <TrendingDown className="w-3.5 h-3.5 text-loss" />
    } else {
      return <Minus className="w-3.5 h-3.5 text-gray-600 dark:text-gray-400" />
    }
  }

  const getChangeColor = (changePercent: number) => {
    if (changePercent > 0) return 'text-gain'
    if (changePercent < 0) return 'text-loss'
    return 'text-gray-600 dark:text-gray-400'
  }

  const getFearGreedColors = (score: number) => {
    if (score >= 75) {
      return {
        textColor: 'text-gain',
        bgColor: 'bg-green-50 dark:bg-green-900/30',
        borderColor: 'border-green-100 dark:border-green-700/50'
      }
    } else if (score >= 55) {
      return {
        textColor: 'text-lime-600 dark:text-lime-400',
        bgColor: 'bg-lime-50 dark:bg-lime-900/30',
        borderColor: 'border-lime-100 dark:border-lime-700/50'
      }
    } else if (score >= 45) {
      return {
        textColor: 'text-gray-600 dark:text-gray-400',
        bgColor: 'bg-gray-50 dark:bg-gray-900/30',
        borderColor: 'border-gray-100 dark:border-gray-700/50'
      }
    } else if (score >= 25) {
      return {
        textColor: 'text-orange-600 dark:text-orange-400',
        bgColor: 'bg-orange-50 dark:bg-orange-900/30',
        borderColor: 'border-orange-100 dark:border-orange-700/50'
      }
    } else {
      return {
        textColor: 'text-loss',
        bgColor: 'bg-red-50 dark:bg-red-900/30',
        borderColor: 'border-red-100 dark:border-red-700/50'
      }
    }
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 16, scale: 0.98 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ type: 'spring', stiffness: 320, damping: 26, mass: 0.7 }}
      className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-3"
    >
      {/* <h3 className="text-xs font-bold text-gray-600 dark:text-gray-400 mb-2">
        Stock Indices
      </h3> */}

      {/* Indices List - Super Compact */}
      <div className="flex flex-wrap gap-2">
        {/* Fear & Greed Index */}
        {fearGreedLoading ? (
          <div className="flex items-center gap-1.5 px-2.5 py-1.5 bg-gray-50 dark:bg-gray-900/30 rounded-lg border border-gray-100 dark:border-gray-700/50 animate-pulse">
            <div className="w-3.5 h-3.5 bg-gray-200 dark:bg-gray-700 rounded"></div>
            <div className="w-20 h-3 bg-gray-200 dark:bg-gray-700 rounded"></div>
            <div className="text-gray-300 dark:text-gray-400">|</div>
            <div className="w-8 h-4 bg-gray-200 dark:bg-gray-700 rounded"></div>
            <div className="w-12 h-2.5 bg-gray-200 dark:bg-gray-700 rounded"></div>
          </div>
        ) : fearGreed ? (
          <a
            href="https://www.cnn.com/markets/fear-and-greed"
            target="_blank"
            rel="noopener noreferrer"
            className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border ${
              getFearGreedColors(fearGreed.score).bgColor
            } ${getFearGreedColors(fearGreed.score).borderColor} hover:opacity-80 transition-opacity cursor-pointer`}
          >
            <Activity className={`w-3.5 h-3.5 ${getFearGreedColors(fearGreed.score).textColor}`} />
            <span className="text-xs font-medium text-gray-700 dark:text-gray-300">
              Fear & Greed
            </span>
            <span className="text-gray-300 dark:text-gray-400">|</span>
            <span className={`text-sm font-bold ${getFearGreedColors(fearGreed.score).textColor}`}>
              {fearGreed.score.toFixed(0)}
            </span>
            <span className="text-[10px] text-gray-500 dark:text-gray-400">
              {fearGreed.rating}
            </span>
          </a>
        ) : null}

        {indices.map((index, i) => (
          <div
            key={i}
            className="relative"
          >
            <div
              className="flex items-center gap-1.5 px-2.5 py-1.5 bg-gray-50 dark:bg-gray-900/30 rounded-lg border border-gray-100 dark:border-gray-700/50 cursor-default"
              onMouseEnter={() => {
                if (index.name === 'S&P 500 VIX') setShowVixPopover(true)
              }}
              onMouseLeave={() => {
                if (index.name === 'S&P 500 VIX') setShowVixPopover(false)
              }}
            >
              {index.loading ? (
                <>
                  {/* Name */}
                  <span className="text-xs font-medium text-gray-700 dark:text-gray-300">
                    {index.name}
                  </span>

                  {/* Divider */}
                  <span className="text-gray-300 dark:text-gray-400">|</span>

                  {/* Loading skeleton for value only */}
                  <div className="flex items-center gap-1 animate-pulse">
                    <div className="w-3.5 h-3.5 bg-gray-200 dark:bg-gray-700 rounded"></div>
                    <div className="w-12 h-4 bg-gray-200 dark:bg-gray-700 rounded"></div>
                  </div>
                </>
              ) : index.error ? (
                <div className="text-xs text-loss">Error</div>
              ) : (
                <>
                  {/* Name */}
                  <span className="text-xs font-medium text-gray-700 dark:text-gray-300">
                    {index.name}
                  </span>

                  {/* Divider */}
                  <span className="text-gray-300 dark:text-gray-400">|</span>

                  {/* Change */}
                  <div className={`flex items-center gap-1 ${index.name === 'S&P 500 VIX' ? 'text-gray-700 dark:text-gray-300' : getChangeColor(index.changePercent)}`}>
                    {index.name !== 'S&P 500 VIX' && renderChangeIndicator(index.changePercent)}
                    <span className="text-sm font-bold">
                      {index.name === 'S&P 500 VIX' ? (
                        // For VIX, show the absolute value without % or +/- sign
                        index.changePercent.toFixed(2)
                      ) : (
                        // For others, show percentage change with sign
                        <>
                          {index.changePercent > 0 && '+'}
                          {index.changePercent.toFixed(2)}%
                        </>
                      )}
                    </span>
                  </div>
                </>
              )}
            </div>

            {/* VIX Popover */}
            {index.name === 'S&P 500 VIX' && showVixPopover && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className="absolute z-50 bottom-full mb-2 left-1/2 transform -translate-x-1/2 w-72 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-xl p-3"
              >
                {/* Arrow */}
                <div className="absolute top-full left-1/2 transform -translate-x-1/2 -mt-[1px]">
                  <div className="border-8 border-transparent border-t-white dark:border-t-gray-800"></div>
                </div>

                <h4 className="text-xs font-bold text-gray-700 dark:text-gray-300 mb-2">
                  การตีความค่า VIX
                </h4>

                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b border-gray-200 dark:border-gray-700">
                      <th className="text-left py-1 px-2 font-semibold text-gray-600 dark:text-gray-400">
                        ช่วงค่า VIX
                      </th>
                      <th className="text-left py-1 px-2 font-semibold text-gray-600 dark:text-gray-400">
                        การตีความโดยทั่วไป
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-b border-gray-100 dark:border-gray-800">
                      <td className="py-1.5 px-2 text-gray-700 dark:text-gray-300">
                        ต่ำกว่า 15
                      </td>
                      <td className="py-1.5 px-2 text-gray-600 dark:text-gray-400">
                        ตลาดสงบ ความกลัวน้อย
                      </td>
                    </tr>
                    <tr className="border-b border-gray-100 dark:border-gray-800">
                      <td className="py-1.5 px-2 text-gray-700 dark:text-gray-300">
                        15–25
                      </td>
                      <td className="py-1.5 px-2 text-gray-600 dark:text-gray-400">
                        ตลาดปกติ มีการเคลื่อนไหวบ้าง
                      </td>
                    </tr>
                    <tr className="border-b border-gray-100 dark:border-gray-800">
                      <td className="py-1.5 px-2 text-gray-700 dark:text-gray-300">
                        สูงกว่า 25
                      </td>
                      <td className="py-1.5 px-2 text-gray-600 dark:text-gray-400">
                        ตลาดเริ่มกังวล มีความกลัวเพิ่มขึ้น
                      </td>
                    </tr>
                    <tr>
                      <td className="py-1.5 px-2 text-gray-700 dark:text-gray-300">
                        สูงกว่า 40
                      </td>
                      <td className="py-1.5 px-2 text-gray-600 dark:text-gray-400">
                        ตลาดตื่นตระหนก เช่น ช่วง COVID หรือวิกฤตการเงิน
                      </td>
                    </tr>
                  </tbody>
                </table>
              </motion.div>
            )}
          </div>
        ))}
      </div>
    </motion.div>
  )
}

export default MarketIndices
